export const en = {
  meta: { name: 'English', dir: 'ltr' as 'ltr' | 'rtl', code: 'en', voice: 'en-US' },

  app: {
    name: 'NabuSu',
    tagline: 'Someone awake with you.',
  },

  common: {
    continue: 'Continue',
    back: 'Back',
    done: 'Done',
    skip: 'Skip',
    close: 'Close',
    save: 'Save',
    cancel: 'Cancel',
    stop: 'Stop',
    start: 'Start',
    next: 'Next',
    minutes: 'min',
    today: 'Today',
    yesterday: 'Yesterday',
    none: 'None',
    optional: 'optional',
  },

  onboarding: {
    step1Title: 'This is NabuSu.',
    step1Body:
      'Nabu was the Babylonian scribe who kept the tablets of destiny. NabuSu keeps the record of your nights — until you can sleep again on your own.',
    step1Sub: 'It was built by someone who spent a week awake.',

    step2Title: 'What this is not.',
    step2Body:
      'NabuSu is not a doctor, not a therapist, and not a medicine. It cannot diagnose you and it will never tell you to change a dose.',
    step2Body2:
      'What it can do is be awake with you at 3am, give your body something to do, and keep a record you can show a real clinician.',

    step3Title: 'Read this once. It matters.',
    step3Body:
      'Stopping benzodiazepines suddenly — lorazepam, alprazolam, clonazepam, diazepam — can cause seizures, and it can kill. This is one of the few drug classes where cold turkey is genuinely dangerous.',
    step3Body2:
      'If you are on them, do not stop on your own. Ask a doctor for a slow taper. If you have already run out and cannot get more, treat that as urgent medical care, not willpower.',
    step3Ack: 'I understand. I will not stop abruptly on my own.',

    step4Title: 'Why are you here tonight?',
    step4Body: 'This only changes the tone. You can change it any time.',
    reasonInsomnia: 'I cannot sleep',
    reasonInsomniaSub: 'No medication involved',
    reasonTapering: 'I am tapering, with a doctor',
    reasonTaperingSub: 'Reducing slowly under supervision',
    reasonForced: 'I ran out and had no choice',
    reasonForcedSub: 'Cut off without a plan',
    reasonCurious: 'Just looking around',
    reasonCuriousSub: 'Nothing urgent',

    forcedWarnTitle: 'Then please read this.',
    forcedWarnBody:
      'Losing access to a benzodiazepine without a taper is a medical situation, not a test of character. Seizure risk is highest in the first few days. Contact a doctor, a clinic, or emergency services and tell them plainly: you were taking a benzodiazepine and you have run out.',
    forcedWarnBody2:
      'NabuSu will stay with you through the nights. It cannot replace that call.',

    step5Title: 'What time do you want me to show up?',
    step5Body: 'Every night, a little before you would normally try to sleep.',

    ready: 'I am ready',
  },

  home: {
    greetingNight: 'It is late.',
    greetingDeepNight: 'The hard hours.',
    greetingEvening: 'The evening is closing.',
    greetingMorning: 'You made it to morning.',
    greetingDay: 'Rest is built during the day too.',

    nightsLogged: (n: number) => (n === 1 ? '1 night recorded' : `${n} nights recorded`),
    nothingYet: 'No nights recorded yet.',

    ritual: 'Tonight’s ritual',
    ritualSub: 'Ten minutes. Pick a door.',

    breath: 'Breath',
    breathSub: 'Slow the body down first',
    mantra: 'Mantra',
    mantraSub: 'Give the mind one sentence',
    drift: 'Drift',
    driftSub: 'Somewhere else for a while',
    sound: 'Sound',
    soundSub: 'Rain, drone, hush',

    rescue: '3AM',
    rescueSub: 'Awake and frightened? Press this.',

    companion: 'Talk',
    ledger: 'Ledger',
    checkin: 'Morning check-in',
    settings: 'Settings',
  },

  breath: {
    title: 'Breath',
    pick: 'Pick a rhythm',
    inhale: 'In',
    hold: 'Hold',
    exhale: 'Out',
    holdOut: 'Empty',
    rounds: (n: number) => `Round ${n}`,
    finishedTitle: 'That is enough.',
    finishedBody: 'You do not have to feel different. You only had to do it.',
    guideVoice: 'Voice guidance',
  },

  mantra: {
    title: 'Mantra',
    pick: 'Choose a sentence to keep',
    own: 'Write your own',
    ownPlaceholder: 'One short sentence. Present tense.',
    repeating: 'Repeating',
    tapToStop: 'Tap anywhere to stop',
    speed: 'Pace',
    slow: 'Slow',
    medium: 'Medium',
    fast: 'Steady',
  },

  drift: {
    title: 'Drift',
    pick: 'Where do you want to go?',
    playing: 'Playing',
  },

  sound: {
    title: 'Sound',
    off: 'Off',
    brown: 'Deep noise',
    rain: 'Rain',
    drone: 'Drone',
    hush: 'Hush',
    heart: 'Slow heartbeat',
    volume: 'Volume',
    timer: 'Fade out after',
    noTimer: 'Do not fade',
  },

  night3am: {
    title: 'You are awake at the worst hour.',
    intro: 'Do not try to sleep. That is not the job right now. The job is to get through this hour.',
    step: (n: number, total: number) => `${n} of ${total}`,

    s1Title: 'First: you are not in danger from being awake.',
    s1Body:
      'A racing heart, heat, dread, a body that will not settle — this is what a nervous system without its brake looks like. It is horrible. It is not the end of you.',

    s2Title: 'Breathe out longer than you breathe in.',
    s2Body: 'Follow the circle. Nothing else. If you lose it, start again.',

    s3Title: 'Name where you are.',
    s3Body:
      'Five things you can see. Four you can hear. Three you can touch. Say them out loud if you can — the voice pulls you out of the loop faster than thinking does.',

    s4Title: 'One sentence.',
    s4Body: 'Say it until it gets boring. Boring is the goal.',

    s5Title: 'The hour will end.',
    s5Body:
      'It has ended every previous time. Lie still in the dark. Do not check the clock. Not sleeping while resting is still worth something.',

    logged: 'Recorded. You were awake at this hour and you got through it.',
    redFlagTitle: 'Some of this needs a doctor, not an app.',
    redFlagBody:
      'If you have had a seizure, are seeing or hearing things that are not there, are badly confused, cannot stop vomiting, or have a fever with shaking — stop reading and get emergency medical help now. These are not anxiety.',
    emergency: 'This is an emergency',
  },

  ledger: {
    title: 'Ledger',
    sub: 'What actually happened, night by night. Bring this to your doctor.',
    empty: 'Nothing recorded yet. It fills itself as you use the app.',
    addDose: 'Record a dose',
    dose: 'Dose',
    medication: 'Medication',
    amount: 'Amount',
    unit: 'mg',
    slept: 'Slept',
    hours: 'h',
    wokeAt3: 'Woke in the night',
    symptoms: 'Symptoms',
    note: 'Note',
    export: 'Export for my doctor',
    exported: 'Copied. Paste it anywhere.',
    streak: (n: number) => `${n} nights with NabuSu`,
    trendBetter: 'Sleep is trending up over the last two weeks.',
    trendWorse: 'Sleep is trending down. Worth telling your prescriber.',
    trendFlat: 'Roughly steady.',
    notEnough: 'A few more nights and a trend will appear here.',
  },

  checkin: {
    title: 'How was the night?',
    howLong: 'Roughly how long did you sleep?',
    quality: 'How did it feel?',
    q1: 'Awful',
    q2: 'Rough',
    q3: 'Okay',
    q4: 'Good',
    q5: 'Real sleep',
    woke: 'Did you wake in the middle of the night?',
    yes: 'Yes',
    no: 'No',
    symptomsQ: 'Anything in your body? (tap any)',
    saved: 'Recorded.',
    savedSub: 'One more night on the tablet.',
  },

  symptoms: {
    anxiety: 'Anxiety',
    racingHeart: 'Racing heart',
    sweating: 'Sweating',
    tremor: 'Tremor',
    nausea: 'Nausea',
    headache: 'Headache',
    lightSensitivity: 'Light sensitivity',
    soundSensitivity: 'Sound sensitivity',
    muscleTension: 'Muscle tension',
    tinnitus: 'Ringing in ears',
    derealization: 'Feeling unreal',
    dread: 'Dread',
    // red flags — these route to the emergency panel
    seizure: 'Seizure',
    hallucination: 'Seeing or hearing things',
    confusion: 'Severe confusion',
    fever: 'Fever with shaking',
  },

  companion: {
    title: 'Talk',
    sub: 'It is late. Say whatever is in your head.',
    placeholder: 'Type here…',
    offline: 'Offline — answering from what is stored on this device.',
    thinking: 'Listening…',
    disclaimer: 'Not a clinician. Nothing here is medical advice.',
  },

  settings: {
    title: 'Settings',
    language: 'Language',
    ritualTime: 'Nightly ritual time',
    notifications: 'Night reminder',
    notificationsSub: 'A quiet nudge at your ritual time',
    enable: 'Enable',
    enabled: 'On',
    blocked: 'Blocked in your browser settings',
    redShift: 'Warm screen at night',
    redShiftSub: 'Removes blue light after dark',
    voice: 'Spoken guidance',
    haptics: 'Vibration',
    aiEndpoint: 'AI companion endpoint',
    aiEndpointSub: 'Leave empty to stay fully offline',
    reason: 'Why you are here',
    data: 'Your data',
    dataSub: 'Everything stays on this device. Nothing is uploaded.',
    exportAll: 'Export everything',
    wipe: 'Delete everything',
    wipeConfirm: 'Delete every night, dose and note? This cannot be undone.',
    about: 'About',
    safety: 'Safety',
    install: 'Install NabuSu',
    installSub: 'Add to your home screen so it works with no signal',
  },

  safety: {
    title: 'Safety',
    taperTitle: 'Never stop suddenly',
    taperBody:
      'Benzodiazepine withdrawal is one of the few withdrawals that can kill. Seizures, delirium and dangerous blood pressure swings are real risks of abrupt cessation. Any reduction should be slow and supervised by a prescriber. NabuSu will never suggest a dose or a schedule.',
    emergencyTitle: 'Get emergency help if',
    emergencyList: [
      'You have a seizure, or you feel one coming',
      'You see or hear things that are not there',
      'You cannot think straight or do not know where you are',
      'You have a fever with heavy shaking',
      'You cannot keep fluids down',
      'You are thinking about ending your life',
    ],
    emergencyBody:
      'Call your local emergency number, or get to an emergency department. Tell them you were taking a benzodiazepine. That single sentence changes how they treat you.',
    crisisTitle: 'If you are thinking about suicide',
    crisisBody:
      'Please tell a person tonight — a friend, a family member, an emergency line in your country. NabuSu is software. It cannot sit with you the way a human can, and tonight you deserve a human.',
  },

  install: {
    prompt: 'Add NabuSu to your home screen',
    promptSub: 'Then it opens instantly and works with no signal.',
    iosHint: 'Tap Share, then “Add to Home Screen”.',
  },
}

export type Dict = typeof en
